import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppOptionsInterface } from '../../interfaces';
import { environment } from '../../../../environments/environment';
import { SelectItemsInterface } from '../../models';

@Injectable({ providedIn: 'root' })
export class AppOptionsService {
  private optionsSource = new BehaviorSubject<AppOptionsInterface.IAppOptions>(this.getOptions());
  private storageName = 'app-options';
  private storageMainActivity = 'app-main-activity-options';
  private statOptionsStorageName = 'app-stat-options';
  private DSPSSPStatOptionsStorageName = 'dsp-statistic-screen-storage';
  private pmpDealsStorageName = 'pmp-deals-options';

  private pmpDealsOptionsSource = new BehaviorSubject<AppOptionsInterface.IPmpDealsOptions>(this.createPmpDealsOptions());

  constructor() { }

  public get currentOptions(): Observable<AppOptionsInterface.IAppOptions> {
    return this.optionsSource.asObservable();
  }

  public get pmpDealsOptions(): Observable<AppOptionsInterface.IPmpDealsOptions> {
    return this.pmpDealsOptionsSource.asObservable();
  }

  public resetAllToDefaults(): void {
    localStorage.removeItem(this.storageName);
    localStorage.removeItem(this.pmpDealsStorageName);
    this.optionsSource.next(this.getOptions());
    this.pmpDealsOptionsSource.next(this.createPmpDealsOptions());
  }

  public getNurlOptions(): { id: number; name: string }[] {
    return [
      { id: 0, name: 'On win' },
      { id: 1, name: 'On impression' },
    ];
  }

  public getOptionsMainActivity() {
    const defaultOptions = {
      ssp: [
        { id: '-1', name: 'Show All' },
        { id: '1', name: 'Enabled' },
        { id: '0', name: 'Disabled' },
        { id: '2', name: 'Deleted' },
      ],
      dsp: [
        { id: '-1', name: 'Show All' },
        { id: '1', name: 'Enabled' },
        { id: '0', name: 'Disabled' },
        { id: '2', name: 'Deleted' },
      ],
      sspConnections: [
        { id: -1, name: 'All Types' },
        { id: 0, name: 'RTB' },
        { id: 1, name: 'VAST' },
        { id: 2, name: 'XML' },
      ],
      dspConnections: [
        { id: -1, name: 'All Types' },
        { id: 0, name: 'RTB' },
        { id: 1, name: 'VAST' },
        { id: 2, name: 'XML' },
      ],
      ad_formats: [
        { id: '-1', name: 'All Ad formats' },
        { id: 'isBanner', name: 'Banner' },
        { id: 'isNative', name: 'Native' },
        { id: 'isVideo', name: 'Video' },
        { id: 'isAudio', name: 'Audio' },
      ],
      ad_formats_dsp: [
        { id: '-1', name: 'All Ad formats' },
        { id: 'banner', name: 'Banner' },
        { id: 'native', name: 'Native' },
        { id: 'video', name: 'Video' },
        { id: 'audio', name: 'Audio' },
      ],
      sspSelected: '1',
      dspSelected: '1',
      sspSelectedConnection: -1,
      dspSelectedConnection: -1,
      sspSelectedFormats: '-1',
      dspSelectedFormats: '-1',
    };

    const options = localStorage.getItem(this.storageMainActivity);
    if (!options) {
      localStorage.setItem(this.storageMainActivity, JSON.stringify(defaultOptions));
      return defaultOptions;
    }

    try {
      return JSON.parse(options);
    } catch (e) {
      return defaultOptions;
    }
  }

  public getOptions(): any {
    const defaultOptions: any = {
      ssp: [
        { attr: 'type', label: 'Type', tooltip: 'Endpoint type: RTB endpoint, or VAST, or XML.', active: true },
        { attr: 'formats', label: 'Ad formats', tooltip: 'Ad formats', active: true },
        {
          attr: 'incoming_qps',
          label: 'Incoming QPS',
          tooltip: 'Number of requests (queries per second) received from SSP',
          active: true,
        },
        {
          attr: 'qps',
          label: 'Valid QPS',
          tooltip:
            'Number of requests (queries per second) received from SSP after all validations, scanners blocking, filter lists',
          active: true,
        },
        { attr: 'ssp_rcpm', label: 'SSP RCPM, %', tooltip: 'SSP Spend / SSP Requests * 1000000', active: true },
        {
          attr: 'bidqps',
          label: 'Bid QPS',
          tooltip: 'Real time number of responses (queries per second) that you are sending to the SSP.',
          active: true,
        },
        {
          attr: 'daylySpend',
          label: 'Daily Spend',
          tooltip: 'Daily spend (in $) on this endpoint (in UTC time zone).',
          active: true,
        },
        {
          attr: 'yesterdaySpend',
          label: 'Yesterday Spend',
          tooltip: 'Total spend for the previous day (in $) on this endpoint (in UTC time zone).)',
          active: true,
        },
        { attr: 'ssp_ecpm', label: 'Daily CPM', tooltip: 'SSP eCPM (SSP Spend / Impression *1000)', active: true },
        {
          attr: 'valid_bid_rate',
          label: 'Valid Bid Rate, %',
          tooltip: 'Valid Bid responses / Bid requests * 100',
          active: true,
        },
        { attr: 'win_rate', label: 'Win Rate, %', tooltip: 'SSP wins / DSP wins * 100', active: true },
        { attr: 'render_rate', label: 'Render Rate, %', tooltip: 'Impressions / SSP win * 100', active: true },
        { attr: 'fill_rate', label: 'Fill rate, %', tooltip: 'Impressions / Bid Requests * 100', active: true },
        {
          attr: 'spendLimit',
          label: 'Spend Limit',
          tooltip: 'Daily spend limit of the endpoint (in US dollars).',
          active: true,
        },
        {
          attr: 'margin',
          label: 'Margin, %',
          tooltip: 'Percent that the platform charges from the endpoint revenue.',
          active: true,
        },
        { attr: 'region', label: 'Region', tooltip: 'Data center location.', active: true },
      ],
      dsp: [
        { attr: 'type', label: 'Type', tooltip: 'The endpoint connection type: RTB, VAST, or XML.', active: true },
        { attr: 'formats', label: 'Ad formats', tooltip: 'Ad formats', active: true },
        { attr: 'limitQPS', label: 'Limit QPS', tooltip: 'Max QPS allowed for the endpoint.', active: true },
        { attr: 'dsp_rcpm', label: 'DSP RCPM, %', tooltip: 'DSP Spend / Bid Requests * 1000000', active: true },
        {
          attr: 'realQPS',
          label: 'Real QPS',
          tooltip: 'Real time number of requests (queries per second) received by specific DSP from all linked SSPs.',
          active: true,
        },
        {
          attr: 'bidQPS',
          label: 'Bid QPS',
          tooltip: 'Real time number of bids (queries per second) received from the DSP.',
          active: true,
        },
        {
          attr: 'daylySpend',
          label: 'Daily Spend',
          tooltip: 'Actual daily spend on the endpoint (in US dollars).',
          active: true,
        },
        {
          attr: 'yesterdaySpend',
          label: 'Yesterday Spend',
          tooltip: 'Total spend for the previous day (in $) on this endpoint (in UTC time zone).)',
          active: true,
        },
        { attr: 'dsp_ecpm', label: 'Daily CPM', tooltip: 'DSP eCPM (DSP Spend / Impression *1000)', active: true },
        {
          attr: 'total_bid_rate',
          label: 'Total Bid Rate, %',
          tooltip: 'Total Bid responses / Bid requests * 100',
          active: true,
        },
        { attr: 'win_rate', label: 'Win Rate, %', tooltip: 'DSP wins / Valid DSP responses * 100', active: true },
        { attr: 'fill_rate', label: 'Fill rate, %', tooltip: 'Impressions / Bid Requests * 100', active: true },
        {
          attr: 'spendLimit',
          label: 'Spend Limit',
          tooltip: 'Daily spend limit of the endpoint (in US dollars).',
          active: true,
        },
        {
          attr: 'margin',
          label: 'Margin, %',
          tooltip: 'Percent that the platform charges from the endpoint revenue.',
          active: true,
        },
        { attr: 'region', label: 'Region', tooltip: 'Region of the data center.', active: true },
        {
          attr: 'nurl',
          label: 'Nurl',
          tooltip: 'This setting indicates if you will call Nurls (win notifications) of DSPs.',
          active: true,
        },
      ],
      screen: '',
    };
    if (localStorage.getItem('version') !== '3.0.0') {
      localStorage.removeItem(this.storageName);
      localStorage.removeItem(this.statOptionsStorageName);
      localStorage.removeItem(this.storageMainActivity);
      localStorage.removeItem(this.DSPSSPStatOptionsStorageName);
      localStorage.setItem('version', '3.0.0');
    }

    const options = localStorage.getItem(this.storageName);
    if (!options) {
      localStorage.setItem(this.storageName, JSON.stringify(defaultOptions));
      return defaultOptions;
    }

    try {
      return JSON.parse(options);
    } catch (e) {
      return defaultOptions;
    }
  }

  public setOption(options: any, type: string) {
    const old = this.getOptions();
    old[type as keyof AppOptionsInterface.IAppOptions] = options;
    try {
      localStorage.setItem(this.storageName, JSON.stringify(old));
      this.optionsSource.next(old);
    } catch (e) {
      console.log('error while trying to save options');
    }
  }

  public createPmpDealsOptions(): AppOptionsInterface.IPmpDealsOptions {
    const pmpDealsOptions: AppOptionsInterface.IPmpDealsOptions = {
      ssp: [
        { attr: 'cpm', label: 'Bidfloor', active: true },
        { attr: 'daily_spend', label: 'Daily Spend', active: true },
        { attr: 'yesterday_spend', label: 'Yesterday Spend', active: true },
        { attr: 'incoming_qps', label: 'Incoming QPS', active: true },
        { attr: 'dsp', label: 'DSPs', active: true },
        { attr: 'ssp', label: 'SSPs', active: true },
        { attr: 'platforms', label: 'Ad formats', active: true },
        { attr: 'traffics', label: 'Traffic Types', active: true },
      ],
      dsp: [
        { attr: 'cpm', label: 'Bidfloor', active: true },
        { attr: 'daily_spend', label: 'Daily Spend', active: true },
        { attr: 'yesterday_spend', label: 'Yesterday Spend', active: true },
        { attr: 'incoming_qps', label: 'Incoming QPS', active: true },
        { attr: 'dsp', label: 'DSPs', active: true },
        { attr: 'ssp', label: 'SSPs', active: true },
        { attr: 'platforms', label: 'Ad formats', active: true },
        { attr: 'traffics', label: 'Traffic Types', active: true },
        { attr: 'private_auction', label: 'Private Auction Type', active: true },
      ],
    };

    const options = localStorage.getItem(this.pmpDealsStorageName);

    if (!options) {
      localStorage.setItem(this.pmpDealsStorageName, JSON.stringify(pmpDealsOptions));
      return pmpDealsOptions;
    }

    try {
      const parsedOptions = JSON.parse(options);
      return parsedOptions;
    } catch (e) {
      return pmpDealsOptions;
    }
  }

  public setPmpDealsOptions(options: AppOptionsInterface.IPmpDealsOptions['ssp'] | AppOptionsInterface.IPmpDealsOptions['dsp'], side: 'ssp' | 'dsp') {
    const defaultOptions = this.createPmpDealsOptions();
    defaultOptions[side] = options;
    try {
      localStorage.setItem(this.pmpDealsStorageName, JSON.stringify(defaultOptions));
      this.pmpDealsOptionsSource.next(defaultOptions);
    } catch (e) {
      console.log('error while trying to save options');
    }
  }

  public getApiUrl(): string {
    return environment.apiRoot;
  }

  public getRegions(regions: any): any[] {
    const arrayReturn: any[] = [];
    for (const i in regions) {
      arrayReturn.push({ id: regions[i].region, name: regions[i].region });
    }

    return arrayReturn;
  }

  public getSecureOptions(): { id: number; name: string }[] {
    return [
      { id: 0, name: 'Both' },
      { id: 1, name: 'Secure' },
      { id: 2, name: 'Non Secure' },
    ];
  }

  public getCountriesItems(): SelectItemsInterface.ICountriesItem[] {
    return [
      {
        title: 'North America',
        key: 'NA',
        checked: false,
        children: [
          { title: 'United States', key: 'USA', checked: false, isLeaf: true },
          { title: 'Canada', key: 'CAN', checked: false, isLeaf: true },
        ],
      },
      {
        title: 'LATAM',
        key: 'LATAM',
        checked: false,
        children: [
          { title: 'Argentina', key: 'ARG', checked: false, isLeaf: true },
          { title: 'Bolivia', key: 'BOL', checked: false, isLeaf: true },
          { title: 'Brazil', key: 'BRA', checked: false, isLeaf: true },
          { title: 'Chile', key: 'CHL', checked: false, isLeaf: true },
          { title: 'Colombia', key: 'COL', checked: false, isLeaf: true },
          { title: 'Costa Rica', key: 'CRI', checked: false, isLeaf: true },
          { title: 'Cuba', key: 'CUB', checked: false, isLeaf: true },
          { title: 'Ecuador', key: 'ECU', checked: false, isLeaf: true },
          { title: 'El Salvador', key: 'SLV', checked: false, isLeaf: true },
          { title: 'Guatemala', key: 'GTM', checked: false, isLeaf: true },
          { title: 'Honduras', key: 'HND', checked: false, isLeaf: true },
          { title: 'Mexico', key: 'MEX', checked: false, isLeaf: true },
          { title: 'Nicaragua', key: 'NIC', checked: false, isLeaf: true },
          { title: 'Panama', key: 'PAN', checked: false, isLeaf: true },
          { title: 'Paraguay', key: 'PRY', checked: false, isLeaf: true },
          { title: 'Peru', key: 'PER', checked: false, isLeaf: true },
          { title: 'Uruguay', key: 'URY', checked: false, isLeaf: true },
          { title: 'Venezuela', key: 'VEN', checked: false, isLeaf: true },
          { title: 'Belize', key: 'BLZ', checked: false, isLeaf: true },
          { title: 'Guyana', key: 'GUY', checked: false, isLeaf: true },
          { title: 'Suriname', key: 'SUR', checked: false, isLeaf: true },
          { title: 'French Guiana', key: 'GUF', checked: false, isLeaf: true },
          { title: 'Falkland Islands (Malvinas)', key: 'FLK', checked: false, isLeaf: true },
          {
            title: 'Caribbean',
            key: 'Caribbean',
            checked: false,
            children: [
              { title: 'Antigua and Barbuda', key: 'ATG', checked: false, isLeaf: true },
              { title: 'Aruba', key: 'ABW', checked: false, isLeaf: true },
              { title: 'Bahamas', key: 'BHS', checked: false, isLeaf: true },
              { title: 'Barbados', key: 'BRB', checked: false, isLeaf: true },
              { title: 'Bermuda', key: 'BMU', checked: false, isLeaf: true },
              { title: 'Cayman Islands', key: 'CYM', checked: false, isLeaf: true },
              { title: 'Dominica', key: 'DMA', checked: false, isLeaf: true },
              { title: 'Dominican Republic', key: 'DOM', checked: false, isLeaf: true },
              { title: 'Grenada', key: 'GRD', checked: false, isLeaf: true },
              { title: 'Haiti', key: 'HTI', checked: false, isLeaf: true },
              { title: 'Jamaica', key: 'JAM', checked: false, isLeaf: true },
              { title: 'Martinique', key: 'MTQ', checked: false, isLeaf: true },
              { title: 'Puerto Rico', key: 'PRI', checked: false, isLeaf: true },
              { title: 'Saint Barthélemy', key: 'BLM', checked: false, isLeaf: true },
              { title: 'Saint Kitts and Nevis', key: 'KNA', checked: false, isLeaf: true },
              { title: 'Saint Lucia', key: 'LCA', checked: false, isLeaf: true },
              { title: 'Saint Vincent and the Grenadines', key: 'VCT', checked: false, isLeaf: true },
              { title: 'Trinidad and Tobago', key: 'TTO', checked: false, isLeaf: true },
              { title: 'Turks and Caicos Islands', key: 'TCA', checked: false, isLeaf: true },
              { title: 'Virgin Islands, British', key: 'VGB', checked: false, isLeaf: true },
              { title: 'Virgin Islands, U.S.', key: 'VIR', checked: false, isLeaf: true },
              { title: 'Anguilla', key: 'AIA', checked: false, isLeaf: true },
              { title: 'Montserrat', key: 'MSR', checked: false, isLeaf: true },
              { title: 'Guadeloupe', key: 'GLP', checked: false, isLeaf: true },
              { title: 'Netherlands Antilles', key: 'ANT', checked: false, isLeaf: true },
              { title: 'Saint Martin (French Part)', key: 'MAF', checked: false, isLeaf: true },
            ],
          },
        ],
      },
      {
        title: 'Europe',
        key: 'EU',
        checked: false,
        children: [
          { title: 'Albania', key: 'ALB', checked: false, isLeaf: true },
          { title: 'Andorra', key: 'AND', checked: false, isLeaf: true },
          { title: 'Austria', key: 'AUT', checked: false, isLeaf: true },
          { title: 'Belgium', key: 'BEL', checked: false, isLeaf: true },
          { title: 'Bosnia and Herzegovina', key: 'BIH', checked: false, isLeaf: true },
          { title: 'Bulgaria', key: 'BGR', checked: false, isLeaf: true },
          { title: 'Belarus', key: 'BLR', checked: false, isLeaf: true },
          { title: 'Croatia', key: 'HRV', checked: false, isLeaf: true },
          { title: 'Czech Republic', key: 'CZE', checked: false, isLeaf: true },
          { title: 'Denmark', key: 'DNK', checked: false, isLeaf: true },
          { title: 'Estonia', key: 'EST', checked: false, isLeaf: true },
          { title: 'Finland', key: 'FIN', checked: false, isLeaf: true },
          { title: 'France', key: 'FRA', checked: false, isLeaf: true },
          { title: 'Germany', key: 'DEU', checked: false, isLeaf: true },
          { title: 'Greece', key: 'GRC', checked: false, isLeaf: true },
          { title: 'Hungary', key: 'HUN', checked: false, isLeaf: true },
          { title: 'Iceland', key: 'ISL', checked: false, isLeaf: true },
          { title: 'Ireland', key: 'IRL', checked: false, isLeaf: true },
          { title: 'Italy', key: 'ITA', checked: false, isLeaf: true },
          { title: 'Latvia', key: 'LVA', checked: false, isLeaf: true },
          { title: 'Liechtenstein', key: 'LIE', checked: false, isLeaf: true },
          { title: 'Lithuania', key: 'LTU', checked: false, isLeaf: true },
          { title: 'Luxembourg', key: 'LUX', checked: false, isLeaf: true },
          { title: 'Moldova', key: 'MDA', checked: false, isLeaf: true },
          { title: 'Monaco', key: 'MCO', checked: false, isLeaf: true },
          { title: 'Montenegro', key: 'MNE', checked: false, isLeaf: true },
          { title: 'Netherlands', key: 'NLD', checked: false, isLeaf: true },
          { title: 'Norway', key: 'NOR', checked: false, isLeaf: true },
          { title: 'Poland', key: 'POL', checked: false, isLeaf: true },
          { title: 'Portugal', key: 'PRT', checked: false, isLeaf: true },
          { title: 'Romania', key: 'ROU', checked: false, isLeaf: true },
          { title: 'Russia', key: 'RUS', checked: false, isLeaf: true },
          { title: 'Serbia', key: 'SRB', checked: false, isLeaf: true },
          { title: 'Slovakia', key: 'SVK', checked: false, isLeaf: true },
          { title: 'Slovenia', key: 'SVN', checked: false, isLeaf: true },
          { title: 'Spain', key: 'ESP', checked: false, isLeaf: true },
          { title: 'Sweden', key: 'SWE', checked: false, isLeaf: true },
          { title: 'Switzerland', key: 'CHE', checked: false, isLeaf: true },
          { title: 'Ukraine', key: 'UKR', checked: false, isLeaf: true },
          { title: 'United Kingdom', key: 'GBR', checked: false, isLeaf: true },
          { title: 'Vatican City (Holy See)', key: 'VAT', checked: false, isLeaf: true },
          { title: 'Jersey', key: 'JEY', checked: false, isLeaf: true },
          { title: 'Guernsey', key: 'GGY', checked: false, isLeaf: true },
          { title: 'Isle of Man', key: 'IMN', checked: false, isLeaf: true },
          { title: 'Faroe Islands', key: 'FRO', checked: false, isLeaf: true },
          { title: 'Gibraltar', key: 'GIB', checked: false, isLeaf: true },
          { title: 'Åland Islands', key: 'ALA', checked: false, isLeaf: true },
          { title: 'North Macedonia', key: 'MKD', checked: false, isLeaf: true },
          { title: 'San Marino', key: 'SMR', checked: false, isLeaf: true },
          { title: 'Malta', key: 'MLT', checked: false, isLeaf: true },
          { title: 'Cyprus', key: 'CYP', checked: false, isLeaf: true },
          { title: 'Greenland', key: 'GRL', checked: false, isLeaf: true },
          { title: 'Kosovo', key: 'XKX', checked: false, isLeaf: true }
        ],
      },
      {
        title: 'Middle East & North Africa (MENA)',
        key: 'MENA',
        checked: false,
        children: [
          { title: 'Saudi Arabia', key: 'SAU', checked: false, isLeaf: true },
          { title: 'United Arab Emirates', key: 'ARE', checked: false, isLeaf: true },
          { title: 'Qatar', key: 'QAT', checked: false, isLeaf: true },
          { title: 'Kuwait', key: 'KWT', checked: false, isLeaf: true },
          { title: 'Oman', key: 'OMN', checked: false, isLeaf: true },
          { title: 'Bahrain', key: 'BHR', checked: false, isLeaf: true },
          { title: 'Israel', key: 'ISR', checked: false, isLeaf: true },
          { title: 'Palestine', key: 'PSE', checked: false, isLeaf: true },
          { title: 'Jordan', key: 'JOR', checked: false, isLeaf: true },
          { title: 'Lebanon', key: 'LBN', checked: false, isLeaf: true },
          { title: 'Iraq', key: 'IRQ', checked: false, isLeaf: true },
          { title: 'Iran', key: 'IRN', checked: false, isLeaf: true },
          { title: 'Syria', key: 'SYR', checked: false, isLeaf: true },
          { title: 'Yemen', key: 'YEM', checked: false, isLeaf: true },
          { title: 'Egypt', key: 'EGY', checked: false, isLeaf: true },
          { title: 'Libya', key: 'LBY', checked: false, isLeaf: true },
          { title: 'Tunisia', key: 'TUN', checked: false, isLeaf: true },
          { title: 'Algeria', key: 'DZA', checked: false, isLeaf: true },
          { title: 'Morocco', key: 'MAR', checked: false, isLeaf: true },
          { title: 'Western Sahara', key: 'ESH', checked: false, isLeaf: true },
          { title: 'Sudan', key: 'SDN', checked: false, isLeaf: true },
          { title: 'South Sudan', key: 'SSD', checked: false, isLeaf: true },
          { title: 'Djibouti', key: 'DJI', checked: false, isLeaf: true },
          { title: 'Somalia', key: 'SOM', checked: false, isLeaf: true },
          { title: 'Eritrea', key: 'ERI', checked: false, isLeaf: true },
          { title: 'Armenia', key: 'ARM', checked: false, isLeaf: true },
          { title: 'Azerbaijan', key: 'AZE', checked: false, isLeaf: true },
          { title: 'Georgia', key: 'GEO', checked: false, isLeaf: true },
          { title: 'Turkey', key: 'TUR', checked: false, isLeaf: true },
        ],
      },
      {
        title: 'Sub-Saharan Africa',
        key: 'SSA',
        checked: false,
        children: [
          { title: 'South Africa', key: 'ZAF', checked: false, isLeaf: true },
          { title: 'Nigeria', key: 'NGA', checked: false, isLeaf: true },
          { title: 'Kenya', key: 'KEN', checked: false, isLeaf: true },
          { title: 'Ghana', key: 'GHA', checked: false, isLeaf: true },
          { title: 'Ethiopia', key: 'ETH', checked: false, isLeaf: true },
          { title: 'Uganda', key: 'UGA', checked: false, isLeaf: true },
          { title: 'Tanzania', key: 'TZA', checked: false, isLeaf: true },
          { title: 'Zimbabwe', key: 'ZWE', checked: false, isLeaf: true },
          { title: 'Zambia', key: 'ZMB', checked: false, isLeaf: true },
          { title: 'Botswana', key: 'BWA', checked: false, isLeaf: true },
          { title: 'Namibia', key: 'NAM', checked: false, isLeaf: true },
          { title: 'Malawi', key: 'MWI', checked: false, isLeaf: true },
          { title: 'Mozambique', key: 'MOZ', checked: false, isLeaf: true },
          { title: 'Angola', key: 'AGO', checked: false, isLeaf: true },
          { title: 'Benin', key: 'BEN', checked: false, isLeaf: true },
          { title: 'Burkina Faso', key: 'BFA', checked: false, isLeaf: true },
          { title: 'Burundi', key: 'BDI', checked: false, isLeaf: true },
          { title: 'Cameroon', key: 'CMR', checked: false, isLeaf: true },
          { title: 'Central African Republic', key: 'CAF', checked: false, isLeaf: true },
          { title: 'Chad', key: 'TCD', checked: false, isLeaf: true },
          { title: 'Congo (Republic)', key: 'COG', checked: false, isLeaf: true },
          { title: 'Democratic Republic of the Congo (DRC)', key: 'COD', checked: false, isLeaf: true },
          { title: "Côte d'Ivoire", key: 'CIV', checked: false, isLeaf: true },
          { title: 'Equatorial Guinea', key: 'GNQ', checked: false, isLeaf: true },
          { title: 'Eswatini', key: 'SWZ', checked: false, isLeaf: true },
          { title: 'Gabon', key: 'GAB', checked: false, isLeaf: true },
          { title: 'Gambia', key: 'GMB', checked: false, isLeaf: true },
          { title: 'Guinea', key: 'GIN', checked: false, isLeaf: true },
          { title: 'Guinea-Bissau', key: 'GNB', checked: false, isLeaf: true },
          { title: 'Lesotho', key: 'LSO', checked: false, isLeaf: true },
          { title: 'Liberia', key: 'LBR', checked: false, isLeaf: true },
          { title: 'Madagascar', key: 'MDG', checked: false, isLeaf: true },
          { title: 'Mali', key: 'MLI', checked: false, isLeaf: true },
          { title: 'Mauritania', key: 'MRT', checked: false, isLeaf: true },
          { title: 'Mauritius', key: 'MUS', checked: false, isLeaf: true },
          { title: 'Niger', key: 'NER', checked: false, isLeaf: true },
          { title: 'Rwanda', key: 'RWA', checked: false, isLeaf: true },
          { title: 'Senegal', key: 'SEN', checked: false, isLeaf: true },
          { title: 'Seychelles', key: 'SYC', checked: false, isLeaf: true },
          { title: 'Sierra Leone', key: 'SLE', checked: false, isLeaf: true },
          { title: 'São Tomé and Príncipe', key: 'STP', checked: false, isLeaf: true },
          { title: 'Togo', key: 'TGO', checked: false, isLeaf: true },
          { title: 'Comoros', key: 'COM', checked: false, isLeaf: true },
          { title: 'Réunion', key: 'REU', checked: false, isLeaf: true },
          { title: 'Mayotte', key: 'MYT', checked: false, isLeaf: true },
          { title: 'Cape Verde', key: 'CPV', checked: false, isLeaf: true },
          { title: 'Saint Helena, Ascension and Tristan da Cunha', key: 'SHN', checked: false, isLeaf: true },
        ],
      },
      {
        title: 'APAC',
        key: 'APAC',
        checked: false,
        children: [
          { title: 'Australia', key: 'AUS', checked: false, isLeaf: true },
          { title: 'New Zealand', key: 'NZL', checked: false, isLeaf: true },
          { title: 'Japan', key: 'JPN', checked: false, isLeaf: true },
          { title: 'South Korea', key: 'KOR', checked: false, isLeaf: true },
          { title: 'Singapore', key: 'SGP', checked: false, isLeaf: true },
          { title: 'Taiwan', key: 'TWN', checked: false, isLeaf: true },
          { title: 'Hong Kong', key: 'HKG', checked: false, isLeaf: true },
          { title: 'China', key: 'CHN', checked: false, isLeaf: true },
          { title: 'India', key: 'IND', checked: false, isLeaf: true },
          { title: 'Indonesia', key: 'IDN', checked: false, isLeaf: true },
          { title: 'Philippines', key: 'PHL', checked: false, isLeaf: true },
          { title: 'Thailand', key: 'THA', checked: false, isLeaf: true },
          { title: 'Vietnam', key: 'VNM', checked: false, isLeaf: true },
          { title: 'Pakistan', key: 'PAK', checked: false, isLeaf: true },
          { title: 'Bangladesh', key: 'BGD', checked: false, isLeaf: true },
          { title: 'Myanmar', key: 'MMR', checked: false, isLeaf: true },
          { title: 'Cambodia', key: 'KHM', checked: false, isLeaf: true },
          { title: 'Sri Lanka', key: 'LKA', checked: false, isLeaf: true },
          { title: 'Nepal', key: 'NPL', checked: false, isLeaf: true },
          { title: 'Laos', key: 'LAO', checked: false, isLeaf: true },
          { title: 'Mongolia', key: 'MNG', checked: false, isLeaf: true },
          { title: 'Papua New Guinea', key: 'PNG', checked: false, isLeaf: true },
          { title: 'Fiji', key: 'FJI', checked: false, isLeaf: true },
          { title: 'Samoa', key: 'WSM', checked: false, isLeaf: true },
          { title: 'Solomon Islands', key: 'SLB', checked: false, isLeaf: true },
          { title: 'Vanuatu', key: 'VUT', checked: false, isLeaf: true },
          { title: 'Tonga', key: 'TON', checked: false, isLeaf: true },
          { title: 'Palau', key: 'PLW', checked: false, isLeaf: true },
          { title: 'Micronesia', key: 'FSM', checked: false, isLeaf: true },
          { title: 'Northern Mariana Islands', key: 'MNP', checked: false, isLeaf: true },
          { title: 'Guam', key: 'GUM', checked: false, isLeaf: true },
          { title: 'American Samoa', key: 'ASM', checked: false, isLeaf: true },
          { title: 'Timor-Leste', key: 'TLS', checked: false, isLeaf: true },
          { title: 'Cook Islands', key: 'COK', checked: false, isLeaf: true },
          { title: 'Kiribati', key: 'KIR', checked: false, isLeaf: true },
          { title: 'Marshall Islands', key: 'MHL', checked: false, isLeaf: true },
          { title: 'Tuvalu', key: 'TUV', checked: false, isLeaf: true },
          { title: 'Wallis and Futuna', key: 'WLF', checked: false, isLeaf: true },
          { title: 'Tokelau', key: 'TKL', checked: false, isLeaf: true },
          { title: 'Norfolk Island', key: 'NFK', checked: false, isLeaf: true },
          { title: 'Niue', key: 'NIU', checked: false, isLeaf: true },
          { title: 'Pitcairn', key: 'PCN', checked: false, isLeaf: true },
          { title: 'Cocos (Keeling) Islands', key: 'CCK', checked: false, isLeaf: true },
          { title: 'Christmas Island', key: 'CXR', checked: false, isLeaf: true },
          { title: 'Heard Island and McDonald Islands', key: 'HMD', checked: false, isLeaf: true },
          { title: 'French Polynesia', key: 'PYF', checked: false, isLeaf: true },
          { title: 'New Caledonia', key: 'NCL', checked: false, isLeaf: true },
          { title: 'Macao', key: 'MAC', checked: false, isLeaf: true },
          { title: 'Brunei Darussalam', key: 'BRN', checked: false, isLeaf: true },
          { title: 'Maldives', key: 'MDV', checked: false, isLeaf: true },
          { title: 'Bhutan', key: 'BTN', checked: false, isLeaf: true },
          { title: 'Afghanistan', key: 'AFG', checked: false, isLeaf: true },
          { title: 'Kazakhstan', key: 'KAZ', checked: false, isLeaf: true },
          { title: 'North Korea', key: 'PRK', checked: false, isLeaf: true },
          { title: 'Kyrgyzstan', key: 'KGZ', checked: false, isLeaf: true },
          { title: 'Malaysia', key: 'MYS', checked: false, isLeaf: true },
          { title: 'Nauru', key: 'NRU', checked: false, isLeaf: true },
          { title: 'Tajikistan', key: 'TJK', checked: false, isLeaf: true },
          { title: 'Turkmenistan', key: 'TKM', checked: false, isLeaf: true },
          { title: 'Uzbekistan', key: 'UZB', checked: false, isLeaf: true },
        ],
      },
      {
        title: 'Other',
        key: 'Other',
        checked: false,
        children: [
          { title: 'Antarctica', key: 'ATA', checked: false, isLeaf: true },
          { title: 'French Southern Territories', key: 'ATF', checked: false, isLeaf: true },
          { title: 'Bouvet Island', key: 'BVT', checked: false, isLeaf: true },
          { title: 'South Georgia and the South Sandwich Islands', key: 'SGS', checked: false, isLeaf: true },
          { title: 'Svalbard and Jan Mayen', key: 'SJM', checked: false, isLeaf: true },
          { title: 'British Indian Ocean Territory', key: 'IOT', checked: false, isLeaf: true },
          { title: 'United States Minor Outlying Islands', key: 'UMI', checked: false, isLeaf: true },
          { title: 'Saint Pierre and Miquelon', key: 'SPM', checked: false, isLeaf: true },
        ],
      },
    ];
  }

  public getNorthAmericaCountriesKeysList(): string[] {
    return ['USA', 'CAN'];
  }

  public getLATAMCountriesKeysList(): string[] {
    return [
      'ARG',
      'BOL',
      'BRA',
      'CHL',
      'COL',
      'CRI',
      'CUB',
      'ECU',
      'SLV',
      'GTM',
      'HND',
      'MEX',
      'NIC',
      'PAN',
      'PRY',
      'PER',
      'URY',
      'VEN',
      'BLZ',
      'GUY',
      'SUR',
      'GUF',
      'FLK',
    ];
  }

  public getCaribbeanCountriesKeysList(): string[] {
    return [
      'ATG',
      'ABW',
      'BHS',
      'BRB',
      'BMU',
      'CYM',
      'DMA',
      'DOM',
      'GRD',
      'HTI',
      'JAM',
      'MTQ',
      'PRI',
      'BLM',
      'KNA',
      'LCA',
      'VCT',
      'TTO',
      'TCA',
      'VGB',
      'VIR',
      'AIA',
      'MSR',
      'GLP',
      'ANT',
      'MAF',
    ];
  }

  public getEuropeCountriesKeysList(): string[] {
    return [
      'ALB',
      'AND',
      'AUT',
      'BEL',
      'BIH',
      'BGR',
      'BLR',
      'HRV',
      'CZE',
      'DNK',
      'EST',
      'FIN',
      'FRA',
      'DEU',
      'GRC',
      'HUN',
      'ISL',
      'IRL',
      'ITA',
      'LVA',
      'LIE',
      'LTU',
      'LUX',
      'MDA',
      'MCO',
      'MNE',
      'NLD',
      'NOR',
      'POL',
      'PRT',
      'ROU',
      'RUS',
      'SRB',
      'SVK',
      'SVN',
      'ESP',
      'SWE',
      'CHE',
      'UKR',
      'GBR',
      'VAT',
      'JEY',
      'GGY',
      'IMN',
      'FRO',
      'GIB',
      'ALA',
      'MKD',
      'SMR',
      'MLT',
      'CYP',
      'GRL',
    ];
  }

  public getMENACountriesKeysList(): string[] {
    return [
      'SAU',
      'ARE',
      'QAT',
      'KWT',
      'OMN',
      'BHR',
      'ISR',
      'PSE',
      'JOR',
      'LBN',
      'IRQ',
      'IRN',
      'SYR',
      'YEM',
      'EGY',
      'LBY',
      'TUN',
      'DZA',
      'MAR',
      'ESH',
      'SDN',
      'SSD',
      'DJI',
      'SOM',
      'ERI',
      'ARM',
      'AZE',
      'GEO',
      'TUR',
    ];
  }

  public getSSACountriesKeysList(): string[] {
    return [
      'ZAF',
      'NGA',
      'KEN',
      'GHA',
      'ETH',
      'UGA',
      'TZA',
      'ZWE',
      'ZMB',
      'BWA',
      'NAM',
      'MWI',
      'MOZ',
      'AGO',
      'BEN',
      'BFA',
      'BDI',
      'CMR',
      'CAF',
      'TCD',
      'COG',
      'COD',
      'CIV',
      'GNQ',
      'SWZ',
      'GAB',
      'GMB',
      'GIN',
      'GNB',
      'LSO',
      'LBR',
      'MDG',
      'MLI',
      'MRT',
      'MUS',
      'NER',
      'RWA',
      'SEN',
      'SYC',
      'SLE',
      'STP',
      'TGO',
      'COM',
      'REU',
      'MYT',
      'CPV',
      'SHN',
    ];
  }

  public getAPACCountriesKeysList(): string[] {
    return [
      'AUS',
      'NZL',
      'JPN',
      'KOR',
      'SGP',
      'TWN',
      'HKG',
      'CHN',
      'IND',
      'IDN',
      'PHL',
      'THA',
      'VNM',
      'PAK',
      'BGD',
      'MMR',
      'KHM',
      'LKA',
      'NPL',
      'LAO',
      'MNG',
      'PNG',
      'FJI',
      'WSM',
      'SLB',
      'VUT',
      'TON',
      'PLW',
      'FSM',
      'MNP',
      'GUM',
      'ASM',
      'TLS',
      'COK',
      'KIR',
      'MHL',
      'TUV',
      'WLF',
      'TKL',
      'NFK',
      'NIU',
      'PCN',
      'CCK',
      'CXR',
      'HMD',
      'PYF',
      'NCL',
      'MAC',
      'BRN',
      'MDV',
      'BTN',
      'AFG',
      'KAZ',
      'PRK',
      'KGZ',
      'MYS',
      'NRU',
      'TJK',
      'TKM',
      'UZB',
    ];
  }

  public getOtherCountriesKeysList(): string[] {
    return ['ATA', 'ATF', 'BVT', 'SGS', 'SJM', 'IOT', 'UMI', 'SPM'];
  }

  public getCountriesList(): { id: string; name: string }[] {
    return [
      { id: 'ABW', name: 'Aruba' },
      { id: 'AFG', name: 'Afghanistan' },
      { id: 'AGO', name: 'Angola' },
      { id: 'AIA', name: 'Anguilla' },
      { id: 'ALA', name: 'Åland Islands' },
      { id: 'ALB', name: 'Albania' },
      { id: 'AND', name: 'Andorra' },
      { id: 'ANT', name: 'Netherlands Antilles' },
      { id: 'ARE', name: 'United Arab Emirates' },
      { id: 'ARG', name: 'Argentina' },
      { id: 'ARM', name: 'Armenia' },
      { id: 'ASM', name: 'American Samoa' },
      { id: 'ATA', name: 'Antarctica' },
      { id: 'ATF', name: 'French Southern Territories' },
      { id: 'ATG', name: 'Antigua and Barbuda' },
      { id: 'AUS', name: 'Australia' },
      { id: 'AUT', name: 'Austria' },
      { id: 'AZE', name: 'Azerbaijan' },
      { id: 'BDI', name: 'Burundi' },
      { id: 'BEL', name: 'Belgium' },
      { id: 'BEN', name: 'Benin' },
      { id: 'BFA', name: 'Burkina Faso' },
      { id: 'BGD', name: 'Bangladesh' },
      { id: 'BGR', name: 'Bulgaria' },
      { id: 'BHR', name: 'Bahrain' },
      { id: 'BHS', name: 'Bahamas' },
      { id: 'BIH', name: 'Bosnia and Herzegovina' },
      { id: 'BLM', name: 'Saint Barthélemy' },
      { id: 'BLR', name: 'Belarus' },
      { id: 'BLZ', name: 'Belize' },
      { id: 'BMU', name: 'Bermuda' },
      { id: 'BOL', name: 'Bolivia, Plurinational State of' },
      { id: 'BRA', name: 'Brazil' },
      { id: 'BRB', name: 'Barbados' },
      { id: 'BRN', name: 'Brunei Darussalam' },
      { id: 'BTN', name: 'Bhutan' },
      { id: 'BVT', name: 'Bouvet Island' },
      { id: 'BWA', name: 'Botswana' },
      { id: 'CAF', name: 'Central African Republic' },
      { id: 'CAN', name: 'Canada' },
      { id: 'CCK', name: 'Cocos (Keeling) Islands' },
      { id: 'CHE', name: 'Switzerland' },
      { id: 'CHL', name: 'Chile' },
      { id: 'CHN', name: 'China' },
      { id: 'CIV', name: "Côte d'Ivoire" },
      { id: 'CMR', name: 'Cameroon' },
      { id: 'COD', name: 'Congo, the Democratic Republic of the' },
      { id: 'COG', name: 'Congo' },
      { id: 'COK', name: 'Cook Islands' },
      { id: 'COL', name: 'Colombia' },
      { id: 'COM', name: 'Comoros' },
      { id: 'CPV', name: 'Cape Verde' },
      { id: 'CRI', name: 'Costa Rica' },
      { id: 'CUB', name: 'Cuba' },
      { id: 'CXR', name: 'Christmas Island' },
      { id: 'CYM', name: 'Cayman Islands' },
      { id: 'CYP', name: 'Cyprus' },
      { id: 'CZE', name: 'Czech Republic' },
      { id: 'DEU', name: 'Germany' },
      { id: 'DJI', name: 'Djibouti' },
      { id: 'DMA', name: 'Dominica' },
      { id: 'DNK', name: 'Denmark' },
      { id: 'DOM', name: 'Dominican Republic' },
      { id: 'DZA', name: 'Algeria' },
      { id: 'ECU', name: 'Ecuador' },
      { id: 'EGY', name: 'Egypt' },
      { id: 'ERI', name: 'Eritrea' },
      { id: 'ESH', name: 'Western Sahara' },
      { id: 'ESP', name: 'Spain' },
      { id: 'EST', name: 'Estonia' },
      { id: 'ETH', name: 'Ethiopia' },
      { id: 'FIN', name: 'Finland' },
      { id: 'FJI', name: 'Fiji' },
      { id: 'FLK', name: 'Falkland Islands (Malvinas)' },
      { id: 'FRA', name: 'France' },
      { id: 'FRO', name: 'Faroe Islands' },
      { id: 'FSM', name: 'Micronesia, Federated States of' },
      { id: 'GAB', name: 'Gabon' },
      { id: 'GBR', name: 'United Kingdom' },
      { id: 'GEO', name: 'Georgia' },
      { id: 'GGY', name: 'Guernsey' },
      { id: 'GHA', name: 'Ghana' },
      { id: 'GIB', name: 'Gibraltar' },
      { id: 'GIN', name: 'Guinea' },
      { id: 'GLP', name: 'Guadeloupe' },
      { id: 'GMB', name: 'Gambia' },
      { id: 'GNB', name: 'Guinea-Bissau' },
      { id: 'GNQ', name: 'Equatorial Guinea' },
      { id: 'GRC', name: 'Greece' },
      { id: 'GRD', name: 'Grenada' },
      { id: 'GRL', name: 'Greenland' },
      { id: 'GTM', name: 'Guatemala' },
      { id: 'GUF', name: 'French Guiana' },
      { id: 'GUM', name: 'Guam' },
      { id: 'GUY', name: 'Guyana' },
      { id: 'HKG', name: 'Hong Kong' },
      { id: 'HMD', name: 'Heard Island and McDonald Islands' },
      { id: 'HND', name: 'Honduras' },
      { id: 'HRV', name: 'Croatia' },
      { id: 'HTI', name: 'Haiti' },
      { id: 'HUN', name: 'Hungary' },
      { id: 'IDN', name: 'Indonesia' },
      { id: 'IMN', name: 'Isle of Man' },
      { id: 'IND', name: 'India' },
      { id: 'IOT', name: 'British Indian Ocean Territory' },
      { id: 'IRL', name: 'Ireland' },
      { id: 'IRN', name: 'Iran, Islamic Republic of' },
      { id: 'IRQ', name: 'Iraq' },
      { id: 'ISL', name: 'Iceland' },
      { id: 'ISR', name: 'Israel' },
      { id: 'ITA', name: 'Italy' },
      { id: 'JAM', name: 'Jamaica' },
      { id: 'JEY', name: 'Jersey' },
      { id: 'JOR', name: 'Jordan' },
      { id: 'JPN', name: 'Japan' },
      { id: 'KAZ', name: 'Kazakhstan' },
      { id: 'KEN', name: 'Kenya' },
      { id: 'KGZ', name: 'Kyrgyzstan' },
      { id: 'KHM', name: 'Cambodia' },
      { id: 'KIR', name: 'Kiribati' },
      { id: 'KNA', name: 'Saint Kitts and Nevis' },
      { id: 'KOR', name: 'Korea, Republic of' },
      { id: 'KWT', name: 'Kuwait' },
      { id: 'LAO', name: "Lao People's Democratic Republic" },
      { id: 'LBN', name: 'Lebanon' },
      { id: 'LBR', name: 'Liberia' },
      { id: 'LBY', name: 'Libyan Arab Jamahiriya' },
      { id: 'LCA', name: 'Saint Lucia' },
      { id: 'LIE', name: 'Liechtenstein' },
      { id: 'LKA', name: 'Sri Lanka' },
      { id: 'LSO', name: 'Lesotho' },
      { id: 'LTU', name: 'Lithuania' },
      { id: 'LUX', name: 'Luxembourg' },
      { id: 'LVA', name: 'Latvia' },
      { id: 'MAC', name: 'Macao' },
      { id: 'MAF', name: 'Saint Martin (French part)' },
      { id: 'MAR', name: 'Morocco' },
      { id: 'MCO', name: 'Monaco' },
      { id: 'MDA', name: 'Moldova, Republic of' },
      { id: 'MDG', name: 'Madagascar' },
      { id: 'MDV', name: 'Maldives' },
      { id: 'MEX', name: 'Mexico' },
      { id: 'MHL', name: 'Marshall Islands' },
      { id: 'MKD', name: 'Macedonia, the former Yugoslav Republic of' },
      { id: 'MLI', name: 'Mali' },
      { id: 'MLT', name: 'Malta' },
      { id: 'MMR', name: 'Myanmar' },
      { id: 'MNE', name: 'Montenegro' },
      { id: 'MNG', name: 'Mongolia' },
      { id: 'MNP', name: 'Northern Mariana Islands' },
      { id: 'MOZ', name: 'Mozambique' },
      { id: 'MRT', name: 'Mauritania' },
      { id: 'MSR', name: 'Montserrat' },
      { id: 'MTQ', name: 'Martinique' },
      { id: 'MUS', name: 'Mauritius' },
      { id: 'MWI', name: 'Malawi' },
      { id: 'MYS', name: 'Malaysia' },
      { id: 'MYT', name: 'Mayotte' },
      { id: 'NAM', name: 'Namibia' },
      { id: 'NCL', name: 'New Caledonia' },
      { id: 'NER', name: 'Niger' },
      { id: 'NFK', name: 'Norfolk Island' },
      { id: 'NGA', name: 'Nigeria' },
      { id: 'NIC', name: 'Nicaragua' },
      { id: 'NIU', name: 'Niue' },
      { id: 'NLD', name: 'Netherlands' },
      { id: 'NOR', name: 'Norway' },
      { id: 'NPL', name: 'Nepal' },
      { id: 'NRU', name: 'Nauru' },
      { id: 'NZL', name: 'New Zealand' },
      { id: 'OMN', name: 'Oman' },
      { id: 'PAK', name: 'Pakistan' },
      { id: 'PAN', name: 'Panama' },
      { id: 'PCN', name: 'Pitcairn' },
      { id: 'PER', name: 'Peru' },
      { id: 'PHL', name: 'Philippines' },
      { id: 'PLW', name: 'Palau' },
      { id: 'PNG', name: 'Papua New Guinea' },
      { id: 'POL', name: 'Poland' },
      { id: 'PRI', name: 'Puerto Rico' },
      { id: 'PRK', name: "Korea, Democratic People's Republic of" },
      { id: 'PRT', name: 'Portugal' },
      { id: 'PRY', name: 'Paraguay' },
      { id: 'PSE', name: 'Palestinian Territory, Occupied' },
      { id: 'PYF', name: 'French Polynesia' },
      { id: 'QAT', name: 'Qatar' },
      { id: 'REU', name: 'Réunion' },
      { id: 'ROU', name: 'Romania' },
      { id: 'RUS', name: 'Russian Federation' },
      { id: 'RWA', name: 'Rwanda' },
      { id: 'SAU', name: 'Saudi Arabia' },
      { id: 'SDN', name: 'Sudan' },
      { id: 'SEN', name: 'Senegal' },
      { id: 'SGP', name: 'Singapore' },
      { id: 'SGS', name: 'South Georgia and the South Sandwich Islands' },
      { id: 'SHN', name: 'Saint Helena, Ascension and Tristan da Cunha' },
      { id: 'SJM', name: 'Svalbard and Jan Mayen' },
      { id: 'SLB', name: 'Solomon Islands' },
      { id: 'SLE', name: 'Sierra Leone' },
      { id: 'SLV', name: 'El Salvador' },
      { id: 'SMR', name: 'San Marino' },
      { id: 'SOM', name: 'Somalia' },
      { id: 'SPM', name: 'Saint Pierre and Miquelon' },
      { id: 'SRB', name: 'Serbia' },
      { id: 'STP', name: 'Sao Tome and Principe' },
      { id: 'SUR', name: 'Suriname' },
      { id: 'SVK', name: 'Slovakia' },
      { id: 'SVN', name: 'Slovenia' },
      { id: 'SWE', name: 'Sweden' },
      { id: 'SWZ', name: 'Swaziland' },
      { id: 'SYC', name: 'Seychelles' },
      { id: 'SYR', name: 'Syrian Arab Republic' },
      { id: 'TCA', name: 'Turks and Caicos Islands' },
      { id: 'TCD', name: 'Chad' },
      { id: 'TGO', name: 'Togo' },
      { id: 'THA', name: 'Thailand' },
      { id: 'TJK', name: 'Tajikistan' },
      { id: 'TKL', name: 'Tokelau' },
      { id: 'TKM', name: 'Turkmenistan' },
      { id: 'TLS', name: 'Timor-Leste' },
      { id: 'TON', name: 'Tonga' },
      { id: 'TTO', name: 'Trinidad and Tobago' },
      { id: 'TUN', name: 'Tunisia' },
      { id: 'TUR', name: 'Turkey' },
      { id: 'TUV', name: 'Tuvalu' },
      { id: 'TWN', name: 'Taiwan, Province of China' },
      { id: 'TZA', name: 'Tanzania, United Republic of' },
      { id: 'UGA', name: 'Uganda' },
      { id: 'UKR', name: 'Ukraine' },
      { id: 'UMI', name: 'United States Minor Outlying Islands' },
      { id: 'URY', name: 'Uruguay' },
      { id: 'USA', name: 'United States' },
      { id: 'UZB', name: 'Uzbekistan' },
      { id: 'VAT', name: 'Holy See (Vatican City State)' },
      { id: 'VCT', name: 'Saint Vincent and the Grenadines' },
      { id: 'VEN', name: 'Venezuela, Bolivarian Republic of' },
      { id: 'VGB', name: 'Virgin Islands, British' },
      { id: 'VIR', name: 'Virgin Islands, U.S.' },
      { id: 'VNM', name: 'Viet Nam' },
      { id: 'VUT', name: 'Vanuatu' },
      { id: 'WLF', name: 'Wallis and Futuna' },
      { id: 'WSM', name: 'Samoa' },
      { id: 'YEM', name: 'Yemen' },
      { id: 'ZAF', name: 'South Africa' },
      { id: 'ZMB', name: 'Zambia' },
      { id: 'ZWE', name: 'Zimbabwe' },
    ];
  }

  public getSizesList(): { id: string; name: string }[] {
    return [
      { id: '1280x720', name: '1280x720' },
      { id: '300x250', name: '300x250' },
      { id: '728x90', name: '728x90' },
      { id: '160x600', name: '160x600' },
      { id: '240x400', name: '240x400' },
      { id: '300x600', name: '300x600' },
      { id: '468x60', name: '468x60' },
      { id: '320x50', name: '320x50' },
      { id: '300x50', name: '300x50' },
      { id: '970x250', name: '970x250' },
      { id: '320x480', name: '320x480' },
    ];
  }

  public getVideoSizesList(): { id: string; name: string }[] {
    return [
      { id: '1280x720', name: '1280x720' },
      { id: '300x250', name: '300x250' },
      { id: '1920x1080', name: '1920x1080' },
      { id: '1090x613', name: '1090x613' },
      { id: '320x480', name: '320x480' },
    ];
  }

  public getListOfSizes(): { id: number; width: number | null; height: number | null; name: string }[] {
    return [
      { id: 0, width: null, height: null, name: 'Custom' },
      { id: 1, width: 320, height: 50, name: 'Standard Banner Phones and Tablets' },
      { id: 2, width: 320, height: 100, name: 'Large Banner Phones and Tablets' },
      { id: 3, width: 300, height: 250, name: 'IAB Medium Rectangle Phones and Tablets' },
      { id: 4, width: 728, height: 90, name: 'IAB Leaderboard Tablets' },
      { id: 5, width: 468, height: 60, name: 'IAB Full-Size Banner Tablets' },
      { id: 6, width: 120, height: 600, name: 'IAB Skyscraper' },
      { id: 7, width: 120, height: 90, name: 'IAB Button 1' },
      { id: 9, width: 120, height: 60, name: 'IAB Button 2' },
      { id: 10, width: 234, height: 60, name: 'IAB Half Banner' },
      { id: 11, width: 88, height: 31, name: 'IAB Micro Bar' },
      { id: 12, width: 125, height: 125, name: 'IAB Square Button' },
      { id: 13, width: 120, height: 240, name: 'IAB Vertical Banner' },
      { id: 14, width: 180, height: 150, name: 'IAB Rectangle' },
      { id: 15, width: 336, height: 280, name: 'IAB Large Rectangle' },
      { id: 16, width: 240, height: 400, name: 'IAB Vertical Rectangle' },
      { id: 17, width: 250, height: 250, name: 'IAB Square Pop - up' },
      { id: 18, width: 160, height: 600, name: 'IAB Wide Skyscraper' },
      { id: 19, width: 720, height: 300, name: 'IAB Pop - Under' },
      { id: 20, width: 300, height: 100, name: 'IAB 3: 1 Rectangle' },
      { id: 21, width: 0, height: 0, name: 'Full - screen Banner' },
    ];
  }

  public getPornFilter(): { id: number; name: string }[] {
    return [
      { id: 0, name: 'Allow All Traffic' },
      { id: 1, name: 'Exclude Adult' },
      { id: 2, name: 'Only Adult' },
    ];
  }

  public getOSList(): { id: string; name: string }[] {
    return [
      { id: 'android', name: 'Android' },
      { id: 'macos', name: 'macOS' },
      { id: 'ios', name: 'iOS' },
      { id: 'windows', name: 'Windows' },
      { id: 'linux', name: 'Linux' },
    ];
  }

  public getConnectionTypes(): { id: number; name: string }[] {
    return [
      { id: 2, name: 'Wifi' },
      { id: 3, name: 'Cellular Network' },
      { id: 4, name: 'Cellular Network - 3G' },
      { id: 5, name: 'Cellular Network - 4G' },
    ];
  }

  public getConnectionTypesForAutologger(): { value: number; label: string; checked?: boolean }[] {
    return [
      { value: 0, label: 'Unknown' },
      { value: 1, label: 'Ethernet' },
      { value: 2, label: 'Wifi' },
      { value: 3, label: 'Cellular Network - Unknown Generation' },
      { value: 4, label: 'Cellular Network - 2G' },
      { value: 5, label: 'Cellular Network - 3G' },
      { value: 6, label: 'Cellular Network - 4G' },
    ];
  }

  public getTrafficTypesForAutologger(): { value: number; label: string; checked?: boolean }[] {
    return [
      { value: 1, label: 'Mobile Web' },
      { value: 2, label: 'Desktop' },
      { value: 3, label: 'Mobile App' },
      { value: 4, label: 'CTV' },
      { value: 5, label: 'Rewarded Video' },
    ];
  }

  public getTypeForAutologger(): { id: string; name: string }[] {
    return [
      { id: 'banner', name: 'Banner' },
      { id: 'video', name: 'Video' },
      { id: 'native', name: 'Native' },
      { id: 'audio', name: 'Audio' },
    ];
  }

  public getDeviceTypesForAutologger(): { value: number; label: string; checked?: boolean }[] {
    return [
      { value: 1, label: 'Mobile/Tablet' },
      { value: 2, label: 'Personal Computer' },
      { value: 3, label: 'Connected TV' },
      { value: 4, label: 'Phone' },
      { value: 5, label: 'Tablet' },
      { value: 6, label: 'Connected Device' },
      { value: 7, label: 'Set Top Box' },
    ];
  }

  public getAggregationsForAutologger(): { id: number; name: string }[] {
    return [
      { id: 1, name: 'SSP' },
      { id: 2, name: 'Sitelist' },
      { id: 3, name: 'SSP+Domains' },
      { id: 4, name: 'Geo' },
      { id: 6, name: 'SSP+Geo' },
      { id: 7, name: 'Domains+Geo' },
    ];
  }

  public getRegionsForAutologger(): { id: string; name: string }[] {
    return [
      { id: 'US_EAST', name: 'US_EAST' },
      { id: 'EU', name: 'EU' },
      { id: 'SGP', name: 'SGP' },
    ];
  }

  public getBrowsersForAutologger(): { id: string; name: string }[] {
    return [
      { id: 'Chrome', name: 'Chrome' },
      { id: 'Edge', name: 'Edge' },
      { id: 'IE', name: 'IE' },
      { id: 'Safari', name: 'Safari' },
      { id: 'Mozilla', name: 'Mozilla Firefox' },
      { id: 'Opera', name: 'Opera' },
      { id: 'UCBrowser', name: 'UC Browser' },
      { id: 'Yandex', name: 'Yandex' },
    ];
  }

  public getCategoriesForAutologger(): { id: string; name: string }[] {
    return [
      { id: 'IAB1', name: 'IAB1 Arts & Entertainment' },
      { id: 'IAB2', name: 'IAB2 Automotive' },
      { id: 'IAB3', name: 'IAB3 Business' },
      { id: 'IAB4', name: 'IAB4 Careers' },
      { id: 'IAB5', name: 'IAB5 Education' },
      { id: 'IAB6', name: 'IAB6 Family & Parenting' },
      { id: 'IAB7', name: 'IAB7 Health & Fitness' },
      { id: 'IAB8', name: 'IAB8 Food & Drink' },
      { id: 'IAB9', name: 'IAB9 Hobbies & Interests' },
      { id: 'IAB10', name: 'IAB10 Home & Garden' },
      { id: 'IAB11', name: "IAB11 Law, Gov't & Politics" },
      { id: 'IAB12', name: 'IAB12 News' },
      { id: 'IAB13', name: 'IAB13 Personal Finance' },
      { id: 'IAB14', name: 'IAB14 Society' },
      { id: 'IAB15', name: 'IAB15 Science' },
      { id: 'IAB16', name: 'IAB16 Pets' },
      { id: 'IAB17', name: 'IAB17 Sports' },
      { id: 'IAB18', name: 'IAB18 Style & Fashion' },
      { id: 'IAB19', name: 'IAB19 Technology & Computing' },
      { id: 'IAB20', name: 'IAB20 Travel' },
      { id: 'IAB21', name: 'IAB21 Real Estate' },
      { id: 'IAB22', name: 'IAB22 Shopping' },
      { id: 'IAB23', name: 'IAB23 Religion & Spirituality' },
    ];
  }

  public getLanguagesForAutologger(): { id: string; name: string }[] {
    return [
      { id: 'ab', name: 'Abkhazian' },
      { id: 'aa', name: 'Afar' },
      { id: 'af', name: 'Afrikaans' },
      { id: 'sq', name: 'Albanian' },
      { id: 'am', name: 'Amharic' },
      { id: 'ar', name: 'Arabic' },
      { id: 'an', name: 'Aragonese' },
      { id: 'hy', name: 'Armenian' },
      { id: 'as', name: 'Assamese' },
      { id: 'ay', name: 'Aymara' },
      { id: 'az', name: 'Azerbaijani' },
      { id: 'ba', name: 'Bashkir' },
      { id: 'eu', name: 'Basque' },
      { id: 'bn', name: 'Bengali (Bangla)' },
      { id: 'dz', name: 'Bhutani' },
      { id: 'bh', name: 'Bihari' },
      { id: 'bi', name: 'Bislama' },
      { id: 'br', name: 'Breton' },
      { id: 'bg', name: 'Bulgarian' },
      { id: 'my', name: 'Burmese' },
      { id: 'be', name: 'Byelorussian (Belarusian)' },
      { id: 'km', name: 'Cambodian' },
      { id: 'ca', name: 'Catalan' },
      { id: 'zh', name: 'Chinese' },
      { id: 'zh-Hans', name: 'Chinese (Simplified)' },
      { id: 'zh-Hant', name: 'Chinese (Traditional)' },
      { id: 'co', name: 'Corsican' },
      { id: 'hr', name: 'Croatian' },
      { id: 'cs', name: 'Czech' },
      { id: 'da', name: 'Danish' },
      { id: 'nl', name: 'Dutch' },
      { id: 'en', name: 'English' },
      { id: 'eo', name: 'Esperanto' },
      { id: 'et', name: 'Estonian' },
      { id: 'fo', name: 'Faeroese' },
      { id: 'fa', name: 'Farsi' },
      { id: 'fj', name: 'Fiji' },
      { id: 'fi', name: 'Finnish' },
      { id: 'fr', name: 'French' },
      { id: 'fy', name: 'Frisian' },
      { id: 'gl', name: 'Galician' },
      { id: 'gd', name: 'Gaelic (Scottish)' },
      { id: 'gv', name: 'Gaelic (Manx)' },
      { id: 'ka', name: 'Georgian' },
      { id: 'de', name: 'German' },
      { id: 'el', name: 'Greek' },
      { id: 'kl', name: 'Greenlandic' },
      { id: 'gn', name: 'Guarani' },
      { id: 'gu', name: 'Gujarati' },
      { id: 'ht', name: 'Haitian Creole' },
      { id: 'ha', name: 'Hausa' },
      { id: 'he', name: 'Hebrew' },
      { id: 'iw', name: 'Hebrew' },
      { id: 'hi', name: 'Hindi' },
      { id: 'hu', name: 'Hungarian' },
      { id: 'is', name: 'Icelandic' },
      { id: 'io', name: 'Ido' },
      { id: 'id', name: 'Indonesian' },
      { id: 'in', name: 'Indonesian' },
      { id: 'ia', name: 'Interlingua' },
      { id: 'ie', name: 'Interlingue' },
      { id: 'iu', name: 'Inuktitut' },
      { id: 'ik', name: 'Inupiak' },
      { id: 'ga', name: 'Irish' },
      { id: 'it', name: 'Italian' },
      { id: 'ja', name: 'Japanese' },
      { id: 'jv', name: 'Javanese' },
      { id: 'kn', name: 'Kannada' },
      { id: 'ks', name: 'Kashmiri' },
      { id: 'kk', name: 'Kazakh' },
      { id: 'rw', name: 'Kinyarwanda (Ruanda)' },
      { id: 'ky', name: 'Kirghiz' },
      { id: 'rn', name: 'Kirundi (Rundi)' },
      { id: 'ko', name: 'Korean' },
      { id: 'ku', name: 'Kurdish' },
      { id: 'lo', name: 'Laothian' },
      { id: 'la', name: 'Latin' },
      { id: 'lv', name: 'Latvian (Lettish)' },
      { id: 'li', name: 'Limburgish ( Limburger)' },
      { id: 'ln', name: 'Lingala' },
      { id: 'lt', name: 'Lithuanian' },
      { id: 'mk', name: 'Macedonian' },
      { id: 'mg', name: 'Malagasy' },
      { id: 'ms', name: 'Malay' },
      { id: 'ml', name: 'Malayalam' },
      { id: 'mt', name: 'Maltese' },
      { id: 'mi', name: 'Maori' },
      { id: 'mr', name: 'Marathi' },
      { id: 'mo', name: 'Moldavian' },
      { id: 'mn', name: 'Mongolian' },
      { id: 'na', name: 'Nauru' },
      { id: 'ne', name: 'Nepali' },
      { id: 'no', name: 'Norwegian' },
      { id: 'oc', name: 'Occitan' },
      { id: 'or', name: 'Oriya' },
      { id: 'om', name: 'Oromo (Afaan Oromo)' },
      { id: 'ps', name: 'Pashto (Pushto)' },
      { id: 'pl', name: 'Polish' },
      { id: 'pt', name: 'Portuguese' },
      { id: 'pa', name: 'Punjabi' },
      { id: 'qu', name: 'Quechua' },
      { id: 'rm', name: 'Rhaeto-Romance' },
      { id: 'ro', name: 'Romanian' },
      { id: 'ru', name: 'Russian' },
      { id: 'sm', name: 'Samoan' },
      { id: 'sg', name: 'Sangro' },
      { id: 'sa', name: 'Sanskrit' },
      { id: 'sr', name: 'Serbian' },
      { id: 'sh', name: 'Serbo-Croatian' },
      { id: 'st', name: 'Sesotho' },
      { id: 'tn', name: 'Setswana' },
      { id: 'sn', name: 'Shona' },
      { id: 'ii', name: 'Sichuan Yi' },
      { id: 'sd', name: 'Sindhi' },
      { id: 'si', name: 'Sinhalese' },
      { id: 'ss', name: 'Siswati' },
      { id: 'sk', name: 'Slovak' },
      { id: 'sl', name: 'Slovenian' },
      { id: 'so', name: 'Somali' },
      { id: 'es', name: 'Spanish' },
      { id: 'su', name: 'Sundanese' },
      { id: 'sw', name: 'Swahili (Kiswahili)' },
      { id: 'sv', name: 'Swedish' },
      { id: 'tl', name: 'Tagalog' },
      { id: 'tg', name: 'Tajik' },
      { id: 'ta', name: 'Tamil' },
      { id: 'tt', name: 'Tatar' },
      { id: 'te', name: 'Telugu' },
      { id: 'th', name: 'Thai' },
      { id: 'bo', name: 'Tibetan' },
      { id: 'ti', name: 'Tigrinya' },
      { id: 'to', name: 'Tonga' },
      { id: 'ts', name: 'Tsonga' },
      { id: 'tr', name: 'Turkish' },
      { id: 'tk', name: 'Turkmen' },
      { id: 'tw', name: 'Twi' },
      { id: 'ug', name: 'Uighur' },
      { id: 'uk', name: 'Ukrainian' },
      { id: 'ur', name: 'Urdu' },
      { id: 'uz', name: 'Uzbek' },
      { id: 'vi', name: 'Vietnamese' },
      { id: 'vo', name: 'Volap?k' },
      { id: 'wa', name: 'Wallon' },
      { id: 'cy', name: 'Welsh' },
      { id: 'wo', name: 'Wolof' },
      { id: 'xh', name: 'Xhosa' },
      { id: 'yi', name: 'Yiddish' },
      { id: 'ji', name: 'Yiddish' },
      { id: 'yo', name: 'Yoruba' },
      { id: 'zu', name: 'Zulu' },
    ];
  }
}
